import { listContentVersions, type ContentVersion } from "../../api/content/workflowsApi";
import { toUiError, type UiError } from "../../errors/uiError";
import { readableChangeSetTitle } from "../changeSets/changeSetTitle";

/** The first line of a commit message: its subject, without trailers such as the change set. */
export function messageSubject(message: string): string {
  return message.split("\n", 1)[0]?.trim() ?? message;
}

/** How a merge commit's subject names its change set. */
const MERGE_SUBJECT = /^Merge change set '(.*)'$/su;

/** A model version's name for people: the title of the change set it merged, or its subject. */
export function versionName(message: string): string {
  const subject = messageSubject(message);
  return readableChangeSetTitle(MERGE_SUBJECT.exec(subject)?.[1] ?? subject);
}

/** A project gets its content with its first change set; before that it has no version. */
export function hasNoContentYet(error: UiError): boolean {
  return (
    error.kind === "problem" &&
    error.problem.code === "resource_not_found" &&
    error.problem.details["resource_kind"] === "project_content"
  );
}

/** The project's model versions, newest first; none for a project without content yet. */
export async function listModelVersions(projectId: string, signal: AbortSignal): Promise<readonly ContentVersion[]> {
  try {
    return await listContentVersions(projectId, signal);
  } catch (caught) {
    if (hasNoContentYet(toUiError(caught))) return [];
    throw caught;
  }
}

/** The model version an address names, resolved against the listed versions. */
export interface ResolvedModelVersion {
  readonly commit: string;
  /** The listed version; `undefined` for a pinned version older than the listed page. */
  readonly listed: ContentVersion | undefined;
  readonly isLatest: boolean;
}

/** The chosen version, or the latest for `null`; `null` while the project has no version. */
export function resolveModelVersion(versions: readonly ContentVersion[], chosen: string | null): ResolvedModelVersion | null {
  const latest = versions[0]?.commit ?? null;
  const commit = chosen ?? latest;
  if (commit === null) return null;
  return { commit, listed: versions.find((version) => version.commit === commit), isLatest: commit === latest };
}
